<template>
  <nav
    :class="['journey-timeline', { 'journey-timeline-compact': compact }]"
    aria-label="Journey navigation"
  >
    <div class="journey-header">
      <h2>{{ title }}</h2>
      <p v-if="showDescriptions && intro">{{ intro }}</p>
    </div>

    <div v-if="hasDays" class="journey-days">
      <section
        v-for="day in days"
        :key="`${day.dayLabel}-${day.title}`"
        class="journey-day"
      >
        <h3 class="journey-day-heading">
          <span>{{ day.dayLabel }}</span>
          <strong>{{ day.title }}</strong>
        </h3>

        <ol class="journey-list journey-day-list">
          <li
            v-for="(item, index) in day.items"
            :key="`${day.dayLabel}-${item.title}-${index}`"
            class="journey-item"
          >
            <component
              :is="item.linkedSectionId ? 'a' : 'div'"
              class="journey-card"
              v-bind="journeyCardAttrs(item)"
            >
              <span class="journey-icon">{{ item.icon }}</span>
              <span class="journey-copy">
                <strong>{{ item.title }}</strong>
                <small v-if="showDescriptions && item.subtitle">{{ item.subtitle }}</small>
                <span
                  v-if="shouldShowMemory(item)"
                  :class="['journey-memory', `journey-memory-${item.memory.type}`]"
                >
                  <span class="journey-memory-type">{{ memoryLabel(item.memory.type) }}</span>
                  <span class="journey-memory-title">{{ item.memory.title }}</span>
                  <span class="journey-memory-text">{{ item.memory.text }}</span>
                </span>
              </span>
            </component>
          </li>
        </ol>
      </section>
    </div>

    <ol v-else class="journey-list">
      <li
        v-for="(item, index) in items"
        :key="`${item.title}-${index}`"
        class="journey-item"
      >
        <component
          :is="item.linkedSectionId ? 'a' : 'div'"
          class="journey-card"
          v-bind="journeyCardAttrs(item)"
        >
          <span class="journey-icon">{{ item.icon }}</span>
          <span class="journey-copy">
            <strong>{{ item.title }}</strong>
            <small v-if="showDescriptions && item.subtitle">{{ item.subtitle }}</small>
            <span
              v-if="shouldShowMemory(item)"
              :class="['journey-memory', `journey-memory-${item.memory.type}`]"
            >
              <span class="journey-memory-type">{{ memoryLabel(item.memory.type) }}</span>
              <span class="journey-memory-title">{{ item.memory.title }}</span>
              <span class="journey-memory-text">{{ item.memory.text }}</span>
            </span>
          </span>
        </component>
      </li>
    </ol>

    <p v-if="showDescriptions && closingText" class="journey-closing">{{ closingText }}</p>
  </nav>
</template>

<script>
const memoryTypes = ['unexpected', 'favorite', 'fun-fact'];

const isJourneyItem = (item) => (
  item
  && typeof item.title === 'string'
  && typeof item.icon === 'string'
  && (!item.linkedSectionId || typeof item.linkedSectionId === 'string')
  && (!item.memory || (
    memoryTypes.includes(item.memory.type)
    && typeof item.memory.title === 'string'
    && typeof item.memory.text === 'string'
  ))
);

/**
 * @typedef {Object} JourneyItem
 * @property {string} title
 * @property {string} icon
 * @property {string} [linkedSectionId]
 * @property {{ type: 'unexpected' | 'favorite' | 'fun-fact', title: string, text: string }} [memory]
 */

export default {
  name: 'JourneyTimeline',
  props: {
    title: {
      type: String,
      default: 'The Journey',
    },
    intro: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
      validator: (items) => items.every(isJourneyItem),
    },
    days: {
      type: Array,
      default: () => [],
      validator: (days) => days.every((day) => (
        day
        && typeof day.dayLabel === 'string'
        && typeof day.title === 'string'
        && Array.isArray(day.items)
        && day.items.every(isJourneyItem)
      )),
    },
    closingText: {
      type: String,
      default: '',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    showDescriptions: {
      type: Boolean,
      default: false,
    },
    showMemories: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasDays() {
      return this.days && this.days.length;
    },
  },
  methods: {
    journeyCardAttrs(item) {
      return item.linkedSectionId ? { href: `#${item.linkedSectionId}` } : {};
    },
    shouldShowMemory(item) {
      return Boolean(this.showMemories && item.memory);
    },
    memoryLabel(type) {
      const labels = {
        unexpected: 'Unexpected',
        favorite: 'Favorite',
        'fun-fact': 'Fun fact',
      };

      return labels[type] || 'Memory';
    },
  },
};
</script>

<style scoped>
.journey-timeline {
  background: #0d0d0d;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  margin: 0;
  padding: 16px;
}

.journey-header h2 {
  border-left: 5px solid #e23232;
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 1.16rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.18;
  margin: 0;
  padding-left: 16px;
}

.journey-header p,
.journey-closing {
  color: #c9c9c9;
  font-size: 1.04rem;
  line-height: 1.65;
  margin: 14px 0 0;
}

.journey-list {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  position: relative;
}

.journey-list::before {
  background: #2a2a2a;
  bottom: 14px;
  content: "";
  left: 15px;
  position: absolute;
  top: 14px;
  width: 2px;
}

.journey-days {
  display: grid;
  gap: 18px;
  margin-top: 16px;
}

.journey-day-heading {
  border-bottom: 1px solid #252525;
  display: grid;
  gap: 4px;
  margin: 0 0 10px;
  padding-bottom: 9px;
}

.journey-day-heading span,
.journey-day-heading strong {
  font-family: "Oswald", serif;
  letter-spacing: 0;
  line-height: 1.2;
}

.journey-day-heading span {
  color: #e23232;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.journey-day-heading strong {
  color: #fff;
  font-size: 0.96rem;
  font-weight: 900;
}

.journey-day-list {
  margin-top: 0;
}

.journey-item {
  padding-bottom: 8px;
  padding-left: 40px;
  position: relative;
}

.journey-item:last-child {
  padding-bottom: 0;
}

.journey-icon {
  align-items: center;
  background: #151515;
  border: 1px solid #3a3a3a;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", serif;
  font-size: 0.78rem;
  font-weight: 900;
  height: 30px;
  justify-content: center;
  left: 0;
  line-height: 1;
  position: absolute;
  top: 3px;
  width: 30px;
  z-index: 1;
}

.journey-card {
  background: #080808;
  border: 1px solid #252525;
  border-radius: 8px;
  color: #fff !important;
  display: block;
  min-height: 36px;
  padding: 8px 10px;
  text-decoration: none;
  transition: border-color 160ms ease, transform 160ms ease;
}

a.journey-card:hover,
a.journey-card:focus {
  border-color: #e23232;
  transform: translateX(3px);
}

.journey-copy {
  display: grid;
  gap: 5px;
}

.journey-copy strong {
  color: #fff;
  font-family: "Oswald", serif;
  font-size: 0.9rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
}

.journey-copy small {
  color: #c9c9c9;
  font-size: 0.96rem;
  line-height: 1.45;
}

.journey-memory {
  background: #101010;
  border: 1px solid #2a2a2a;
  border-left: 4px solid #e23232;
  border-radius: 8px;
  display: grid;
  gap: 4px;
  margin-top: 6px;
  padding: 10px;
}

.journey-memory-favorite {
  border-left-color: #f0b13c;
}

.journey-memory-fun-fact {
  border-left-color: #39a8ff;
}

.journey-memory-type,
.journey-memory-title {
  font-family: "Oswald", serif;
  letter-spacing: 0;
  line-height: 1.2;
}

.journey-memory-type {
  color: #f0b13c;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
}

.journey-memory-title {
  color: #fff;
  font-size: 0.92rem;
  font-weight: 900;
}

.journey-memory-text {
  color: #c9c9c9;
  font-size: 0.9rem;
  line-height: 1.45;
}

.journey-closing {
  border-top: 1px solid #252525;
  margin-top: 22px;
  padding-top: 18px;
}

@media (max-width: 1040px) {
  .journey-timeline {
    margin-bottom: 28px;
  }

  .journey-list {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }

  .journey-list::before {
    display: none;
  }

  .journey-item {
    padding: 0;
  }

  .journey-card {
    align-items: center;
    display: flex;
    gap: 9px;
    height: 100%;
  }

  .journey-icon {
    flex: 0 0 auto;
    position: static;
  }
}
</style>
